import { all, call, put, takeLatest } from "redux-saga/effects";
import { normalize, schema } from "normalizr";
import queryString from "query-string";
import { toast } from "react-toastify";
import { USER_AGENTS_FILTER_OPTIONS } from "modules/UserAgents/constants";
import { getErrorMessage } from "utils/sagasErrorHandler";
import { ITEMS_PER_PAGE } from "constants/general";
import messageHelper from "utils/messages/messageHelper";
import { addUserAgentRequest,
    deleteUserAgentRequest,
    getUserAgentsRequest
} from "modules/UserAgents/services/UserAgents/api";
import {
    getUserAgents,
    getUserAgentsSuccess,
    getUserAgentsError,
    addUserAgent,
    mergeAddUserAgentDialog,
    setAddUserAgentDialog,
    deleteUserAgents, exportUserAgents
} from "modules/UserAgents/services/UserAgents/actions";
import * as XLSX from "xlsx";

const scrapeUserAgentsSchema = new schema.Entity("user_agents");
const scrapeUserAgentsListSchema = new schema.Array(scrapeUserAgentsSchema);

//Workers
function* handleRefreshUserAgents() {
    const { search } = window.location;
    yield put(getUserAgents({ limit: ITEMS_PER_PAGE, ...queryString.parse(search) }));
}

function* handleGetUserAgents ({ payload }) {
    try {
        let searchDefault = {
            ...USER_AGENTS_FILTER_OPTIONS,
            ...payload
        };
        const { user_agents, count } = yield call(getUserAgentsRequest, searchDefault);
        const { result, entities } = normalize(user_agents, scrapeUserAgentsListSchema);
        yield put(getUserAgentsSuccess({
            userAgents: entities.user_agents,
            ids: result,
            count
        }));
    } catch (error) {
        yield put(getUserAgentsError());
        getErrorMessage(error);
    }
}

function* handleAddUserAgent ({ payload }) {
    try {
        yield put(mergeAddUserAgentDialog({ isLoading: true }));
        yield call(addUserAgentRequest, payload);

        yield put(setAddUserAgentDialog());
        yield call(toast.success,`The User Agent ${payload.name} has been successfully added`);
        yield handleRefreshUserAgents();
    } catch (error) {
        yield put(mergeAddUserAgentDialog({ isLoading: false }));
        getErrorMessage(error);
    }
}

function* handleDeleteUserAgents ({ payload }) {
    try {
        yield all(payload.map((id) => call(deleteUserAgentRequest, id)));
        yield handleRefreshUserAgents();
        messageHelper({
            type: "success",
            actionSubject: `User Agent${payload.length > 1 ? "s" : ""} with id${payload.length > 1 ? "s" : ""} - ${payload.join(", ")}`,
            action: "deleted",
            plural: payload.length > 1
        });
    } catch (error) {
        getErrorMessage(error);
    }
}

function* handleExportUserAgents ({ payload }) {
    try {
        let searchDefault = {
            ...USER_AGENTS_FILTER_OPTIONS,
            ...payload,
            limit: 100000,
        };
        const { user_agents } = yield call(getUserAgentsRequest, searchDefault);
        const columns = [
            { key: 'name', title: 'Name' },
            { key: 'engine', title: 'Engine' },
            { key: 'major_version', title: 'Major Version' },
            { key: 'tls_client_profile', title: 'TLS Client Profile' },
        ];
        const newDataForExport = user_agents.map(item =>
            columns.reduce((row, { key }) => {
                row[key] = item[key];
                return row;
            }, {})
        );
        const ws = XLSX.utils.json_to_sheet(newDataForExport, {
            header: columns.map(c => c.key),
        });
        XLSX.utils.sheet_add_aoa(
            ws,
            [columns.map(c => c.title)],
            { origin: 'A1' }
        );
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Sheet One');
        XLSX.writeFile(wb, `user_agents.xlsx`);
    } catch (error) {
        getErrorMessage(error);
    }
}

// Watchers
export function* userAgentsEntriesSagas() {
    yield all([
        yield takeLatest(getUserAgents, handleGetUserAgents),
        yield takeLatest(addUserAgent, handleAddUserAgent),
        yield takeLatest(deleteUserAgents, handleDeleteUserAgents),
        yield takeLatest(exportUserAgents, handleExportUserAgents)
    ]);
}
